import InfoIcon from '@mui/icons-material/Info';
import { useCallback, useMemo } from 'react';
import {
  AutocompleteInput,
  ChipField,
  Create,
  Edit,
  Labeled,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  Toolbar,
  required,
  useGetIdentity,
  useGetList,
  useRecordContext,
} from 'react-admin';
import { useLogEffect } from '@/util/useLogEffect';
import { Box, Chip, Tooltip } from '@mui/material';
import { BSON } from 'realm-web';
import { TagInput } from '@/components/TagInput';
import { outputTypesChoices } from './AIPromptsList';

export const entityTypes = {
  therapistSessionNotes: 'Therapist - Session Notes',
  therapistLiveNotes: 'Therapist - Live Notes',
};

export const entityTypesChoices = Object.entries(entityTypes).map(
  ([id, name]) => ({
    id,
    name,
  }),
);

export const AIPromptsEdit = () => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        updatedBy: userId,
        updatedAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Edit
      title={<EditTitle />}
      mutationMode="pessimistic"
      actions={false}
      transform={transform}
    >
      <AIPromptsEditForm />
    </Edit>
  );
};

export const AIPromptsCreate = () => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        id: new BSON.ObjectId().toHexString(),
        // entityType: "therapistSessionNotes",
        createdBy: userId,
        createdAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Create
      redirect="list"
      transform={transform}
      title={<EditTitle />}
    >
      <AIPromptsEditForm />
    </Create>
  );
};

export const EditToolbar = () => (
  <Toolbar>
    <SaveButton label="Save" />
  </Toolbar>
);

export const EditTitle = () => {
  const record = useRecordContext();

  return <span>GPT Prompt: {record ? `${record.id}` : ''}</span>;
};

const AIPromptsEditForm = () => {
  const record = useRecordContext();

  useLogEffect('record', record);

  const { data: aiPromptTags } = useGetList('aiPromptTags', {
    filter: {
      assetType: record?.entityType,
    },
  });

  // Add debugging
  useLogEffect('aiPromptTags raw data', aiPromptTags);

  const availableTags = useMemo(() => {
    const tags = aiPromptTags?.map((tag: any) => tag.tagName);

    console.log('availableTags processed', tags);

    return tags;
  }, [aiPromptTags]);

  // Add debugging
  useLogEffect('availableTags final', availableTags);

  return (
    <SimpleForm record={record} toolbar={<EditToolbar />}>
      {!record && (
        <Box>
          <SelectInput
            fullWidth
            label="Entity Type"
            source="entityType"
            choices={entityTypesChoices}
            validate={[required()]}
          />
        </Box>
      )}
      <Box display="flex" marginBottom="1rem" alignItems="flex-end">
        {!!record && (
          <>
            <Labeled label="Entity Type">
              <Box display="flex" alignItems="center">
                <Chip
                  label={
                    (entityTypes as any)[
                      (record as any)?.entityType ||
                        'therapistSessionNotes'
                    ]
                  }
                />
                {!record && (
                  <Box marginLeft="1rem">
                    <Tooltip title="Creating GPT Prompts is only supported for overriding therapist session notes.">
                      <InfoIcon />
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Labeled>
          </>
        )}

        {record?.entityType?.startsWith('therapist') && (
          <Box marginLeft="1rem">
            <Labeled label="Framework">
              <ReferenceField
                source="subType"
                record={record}
                reference="therapistSessionNotesFrameworks"
                emptyText="General"
              >
                <ChipField source="name" />
              </ReferenceField>
            </Labeled>
          </Box>
        )}

        {!!record && (
          <Box marginLeft="1rem">
            <Labeled label="Output Type">
              <Chip label={record.outputType} />
            </Labeled>
          </Box>
        )}
      </Box>
      {!record && (
        <ReferenceInput
          isRequired
          source="subType"
          reference="therapistSessionNotesFrameworks"
        >
          <AutocompleteInput
            validate={[required()]}
            fullWidth
            label="Framework"
            optionText="name"
          />
        </ReferenceInput>
      )}
      {!record && (
        <SelectInput
          label="Output Type"
          source="outputType"
          fullWidth
          choices={outputTypesChoices}
          validate={[required()]}
        />
      )}
      <TagInput
        label="System Message"
        source="systemMessage"
        validTags={availableTags}
      />
      <TagInput
        label="Prompt Prepend"
        source="promptPrepend"
        validTags={availableTags}
      />
      <TagInput
        label="Prompt Append"
        source="promptAppend"
        validTags={availableTags}
      />
    </SimpleForm>
  );
};
